import { useServiceGraceStatus } from '@/hooks/useServiceGraceStatus';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Clock, AlertTriangle, ShieldOff } from 'lucide-react';
import { differenceInDays, differenceInHours, format } from 'date-fns';

interface ServiceGraceBannerProps {
  serviceKey?: string;
}

/** Service-specific messaging for what's blocked and what may continue */
const SERVICE_GRACE_INFO: Record<string, { blocked: string[]; allowed: string[] }> = {
  ai_interviews: {
    blocked: ['New interview invitations', 'New interview sessions', 'Recruiter interview management'],
    allowed: ['Candidates who opened their link may complete', 'In-progress interviews continue until expiry', 'Submitted interviews will still be scored'],
  },
  candidate_pipeline: {
    blocked: ['New applications processing', 'Stage advancement', 'New offer creation'],
    allowed: ['Sent offers may still be accepted by candidates', 'Viewed offers remain actionable until expiry', 'Hired/rejected records preserved'],
  },
  job_management: {
    blocked: ['New job posting', 'Job editing', 'Job activation'],
    allowed: ['Published jobs remain visible during grace', 'Existing candidate journeys continue', 'Closed/filled jobs preserved'],
  },
  talent_pool: {
    blocked: ['New candidate invitations', 'Talent search', 'New discovery actions'],
    allowed: ['Re-invited candidates may still respond', 'Accepted invitations preserved', 'Historical data remains accessible'],
  },
  team_management: {
    blocked: ['New team invitations', 'Role changes', 'Member management actions'],
    allowed: ['Pending registrations may complete activation', 'Active members retain access', 'Role assignments preserved'],
  },
  cv_review: {
    blocked: ['New CV review requests', 'New AI enhancement requests'],
    allowed: ['Processing reviews will complete', 'Completed reviews remain accessible', 'Applied suggestions preserved in profiles'],
  },
  interview_practice: {
    blocked: ['New practice sessions', 'Practice session creation'],
    allowed: ['In-progress sessions may finish', 'Completed sessions remain accessible'],
  },
};

const DEFAULT_GRACE_INFO = {
  blocked: ['New sends, actions, and usage are blocked'],
  allowed: ['Already-started journeys may complete until expiry', 'Historical records remain in read-only mode'],
};

export function ServiceGraceBanner({ serviceKey }: ServiceGraceBannerProps) {
  const { isInGrace, graceSchedules } = useServiceGraceStatus(serviceKey);

  if (!isInGrace || graceSchedules.length === 0) return null;

  return (
    <div className="space-y-2">
      {graceSchedules.map((schedule) => {
        const expiryDate = new Date(schedule.grace_ends_at);
        const now = new Date();
        const daysRemaining = differenceInDays(expiryDate, now);
        const hoursRemaining = differenceInHours(expiryDate, now);

        const isUrgent = daysRemaining <= 1;
        const serviceName = schedule.service_key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const info = SERVICE_GRACE_INFO[schedule.service_key] || DEFAULT_GRACE_INFO;

        return (
          <Alert
            key={schedule.id}
            variant={isUrgent ? 'destructive' : 'default'}
            className={isUrgent ? '' : 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20'}
          >
            {isUrgent ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <Clock className="h-4 w-4 text-yellow-600" />
            )}
            <AlertTitle className="flex items-center gap-2">
              <ShieldOff className="h-4 w-4" />
              {serviceName} — Expiring
              {daysRemaining > 0
                ? ` in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`
                : hoursRemaining > 0
                  ? ` in ${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'}`
                  : ' — Final hours'}
            </AlertTitle>
            <AlertDescription className="mt-1 text-sm">
              <p>
                This service expires on{' '}
                <strong>{format(expiryDate, 'MMM d, yyyy h:mm a')}</strong>.
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold text-destructive">Blocked:</p>
                  <ul className="mt-0.5 list-disc ps-4 text-xs opacity-80">
                    {info.blocked.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold text-green-700 dark:text-green-400">May continue:</p>
                  <ul className="mt-0.5 list-disc ps-4 text-xs opacity-80">
                    {info.allowed.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-2 text-xs font-medium">
                Renew your plan to restore full access immediately.
              </p>
            </AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
